import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { usePlanRestrictions } from "@/hooks/usePlanRestrictions";
import type { PlanFeature } from "@/hooks/useSubscriptionConfig";

/**
 * Route-level guard: send someone away from a feature they do not have.
 *
 * NOT <FeatureGate>. That one renders an upgrade prompt in place, which is the
 * right answer for a paid tier the owner could buy today. This is for a feature
 * that has not been released to them yet — there is nothing to sell, and a
 * page that says "upgrade to unlock" about something nobody can buy is worse
 * than not being there.
 *
 * WHY THIS EXISTS. The sidebar already hides estimates for an ungranted
 * business, and hiding is not closing: typing /dashboard/estimates loaded the
 * whole page, with real data, for a business whose grant had just been
 * revoked. Verified on stage before writing this. That is the same mistake
 * 20260826210000 caught on the backend — a flag that governs the menu and
 * nothing else.
 *
 * The redirect is deliberately silent. An ungranted business is not supposed
 * to know the feature exists, so it lands on the dashboard rather than on an
 * explanation of what it is missing.
 */
export function RequireFeature({
  feature,
  children,
}: {
  feature: PlanFeature;
  children: ReactNode;
}) {
  const { canUse, isLoading } = usePlanRestrictions();

  // Nothing while the plan resolves. Redirecting first would bounce a granted
  // owner off their own page on every cold load, and rendering first would
  // flash the feature at someone who does not have it.
  if (isLoading) return null;
  if (!canUse(feature)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
